import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import { configService } from '@/services/config.service'
import { AdminLayout } from '@/components/AdminLayout'

export function SettingsPage() {
  const { t } = useTranslation()
  const { token } = useAuthStore()
  const queryClient = useQueryClient()

  const [hourlyRate, setHourlyRate] = useState<number>(20)
  const [bonusPerRegistration, setBonusPerRegistration] = useState<number>(50)
  const [maxDistanceMeters, setMaxDistanceMeters] = useState<number>(200)
  const [saved, setSaved] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['config', 'compensation'],
    queryFn: () => configService.getCompensationConfig(),
  })

  useEffect(() => {
    if (data) {
      setHourlyRate(data.hourlyRate)
      setBonusPerRegistration(data.bonusPerRegistration)
      setMaxDistanceMeters(data.maxDistanceMeters)
    }
  }, [data])

  const saveMutation = useMutation({
    mutationFn: () =>
      configService.updateCompensationConfig(
        { hourlyRate, bonusPerRegistration, maxDistanceMeters },
        token!
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config', 'compensation'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.settings')}</h1>
          <p className="text-gray-600">{t('admin.settingsDesc')}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t('admin.compensationSettings')}
          </h2>

          {isLoading ? (
            <div className="text-gray-600">{t('common.loading')}</div>
          ) : (
            <div className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.hourlyRate')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(Number(e.target.value))}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <span className="text-gray-600">€ / {t('admin.perHourLabel')}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.bonusPerRegistration')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={bonusPerRegistration}
                    onChange={(e) => setBonusPerRegistration(Number(e.target.value))}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <span className="text-gray-600">€ / {t('admin.perRegistrationLabel')}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.maxDistance')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="50"
                    step="50"
                    value={maxDistanceMeters}
                    onChange={(e) => setMaxDistanceMeters(Number(e.target.value))}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <span className="text-gray-600">{t('admin.meters')}</span>
                </div>
              </div>

              <div className="pt-4">
                <button
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saveMutation.isPending ? t('common.saving') : t('common.save')}
                </button>
                {saved && (
                  <span className="ml-3 text-sm text-green-600">{t('common.success')}</span>
                )}
                {saveMutation.isError && (
                  <span className="ml-3 text-sm text-red-600">{t('common.error')}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}
